import { toBlob } from 'html-to-image';

/** 1:1 square share format (e.g. Instagram post). */
export const ACHIEVEMENT_SHARE_CARD_WIDTH = 1080;
export const ACHIEVEMENT_SHARE_CARD_HEIGHT = 1080;

export function getQuizterSiteUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

export type ShareAchievementResult = 'shared' | 'downloaded' | 'cancelled';

function waitNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

/** Same options as share flow — use for preview / tests without calling `navigator.share`. */
export async function captureAchievementShareCard(element: HTMLElement): Promise<Blob> {
  /**
   * With the live node at `left: -12000px`, Chromium often does not lay out/paint it like a
   * normal viewport node; `html-to-image` clones and copies `getComputedStyle`, so the raster
   * can end up as background-only. Briefly move the real node to (0,0) under the modal stack
   * (z-index below typical Bootstrap modal/backdrop), capture, then restore React's inline style.
   */
  const savedStyleAttr = element.getAttribute('style') ?? '';

  const placeForCapture = () => {
    element.style.setProperty('position', 'fixed', 'important');
    element.style.setProperty('left', '0', 'important');
    element.style.setProperty('top', '0', 'important');
    element.style.setProperty('z-index', '1040', 'important');
    element.style.setProperty('opacity', '1', 'important');
    element.style.setProperty('visibility', 'visible', 'important');
    element.style.setProperty('pointer-events', 'none', 'important');
  };

  const restoreStyle = () => {
    if (savedStyleAttr) {
      element.setAttribute('style', savedStyleAttr);
    } else {
      element.removeAttribute('style');
    }
  };

  placeForCapture();
  void element.offsetHeight;
  await waitNextPaint();
  await new Promise<void>((r) => setTimeout(r, 32));
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    await document.fonts.ready.catch(() => undefined);
  }

  try {
    const blob = await toBlob(element, {
      pixelRatio: 1,
      cacheBust: true,
      backgroundColor: '#160D28',
      width: ACHIEVEMENT_SHARE_CARD_WIDTH,
      height: ACHIEVEMENT_SHARE_CARD_HEIGHT,
      skipFonts: false,
      style: {
        position: 'fixed',
        left: '0',
        top: '0',
        margin: '0',
        padding: '0',
        opacity: '1',
        visibility: 'visible',
        zIndex: '1040',
        pointerEvents: 'none',
        overflow: 'hidden',
        contain: 'none',
      },
    });
    if (!blob) {
      throw new Error('Could not generate image');
    }
    return blob;
  } finally {
    restoreStyle();
  }
}

/**
 * Renders `element` to PNG and shares via Web Share API (files when supported),
 * falls back to share URL-only, then download.
 */
export async function shareAchievementCardFromElement(
  element: HTMLElement,
  options: { title: string; text: string; fileName?: string }
): Promise<ShareAchievementResult> {
  const blob = await captureAchievementShareCard(element);

  const fileName = options.fileName || 'quizter-club-achievement.png';
  const file = new File([blob], fileName, { type: blob.type || 'image/png' });
  const nav = navigator as Navigator & {
    share?: (data: ShareData) => Promise<void>;
    canShare?: (data: ShareData) => boolean;
  };

  try {
    if (typeof nav.canShare === 'function' && nav.canShare({ files: [file] })) {
      await nav.share({ title: options.title, text: options.text, files: [file] });
      return 'shared';
    }
  } catch (e: unknown) {
    if ((e as Error).name === 'AbortError') return 'cancelled';
  }

  try {
    if (typeof nav.share === 'function') {
      await nav.share({ title: options.title, text: options.text, url: getQuizterSiteUrl() });
      return 'shared';
    }
  } catch (e: unknown) {
    if ((e as Error).name === 'AbortError') return 'cancelled';
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return 'downloaded';
}

/** Web Share API for a URL (same pattern as achievement fallback share). */
export async function sharePvpInviteLink(options: {
  title: string;
  text: string;
  url: string;
}): Promise<'shared' | 'cancelled'> {
  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  try {
    if (typeof nav.share === 'function') {
      await nav.share({
        title: options.title,
        text: options.text,
        url: options.url,
      });
      return 'shared';
    }
  } catch (e: unknown) {
    if ((e as Error).name === 'AbortError') return 'cancelled';
  }
  throw new Error('Web Share not available');
}
